<script setup lang="ts">
import TheIcon from '@/Components/TheIcon.vue'
import {
    Allowed,
    Amenities,
    Details,
    FeatureItem,
    Features,
    FreebiesIncluded,
    GoodFor,
    Listing,
    Restrictions,
    ReviewsData,
    RoomTypes
} from '../interfaces/listing'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import ListingSwiper from './ListingSwiper.vue'
import AppModal from '@/Components/the-modal/AppModal.vue'
import ListingModalContent from './ListingModalContent.vue'
import { useWindowSize } from '@vueuse/core'
import ListingList from './ListingList.vue'
import { ListingTabs } from '../enums/listingTabs'
import LocationsAtUserWishLists from '@/Components/wishlist/LocationsAtUserWishLists.vue'
import {
    isUserLogged,
    loginEndpoint,
    setAuthorizationStatus,
    toggleIsUserLogged,
    setUserInfo,
    clearAuthorizationFormData,
    errorStatusFromServer
} from '@/Composables/authorization'
import {
    userWishListLocationsIDs,
    setUserWishListLocationsIDs,
    wishlistsIsActiveEndpoint,
    isLoadingRequestWishlist
} from '@/Composables/wishlistLogic'
import TwoFieldsLoginModalContent from '@/Components/the-login/TwoFieldsLoginModalContent.vue'
import { User } from '@/types'
import ComparisonIcon from '@/Components/the-comparison/ComparisonIcon.vue'
import { useEscClose } from '@/Composables/use-esc-close'
import { ContactActiveImports } from '../interfaces/contactActiveImports'
import TheTooltip from '@/Components/the-tooltip/TheTooltip.vue'

type Props = {
    listing: Listing;
    pageFeatures: Features
    pageReviews: ReviewsData,
    listingID: number,
    currentUser: User | null,
    bookHostelsHref: string | undefined,
    hostelIsClosed: boolean,
    pageContactActiveImports: ContactActiveImports
}

const emit = defineEmits(['tabsSwitchAndScroll'])

const props = defineProps<Props>()

const displayedImages = computed(() => {
    if (props.listing && props.listing.images) {
        return props.listing.images.slice(3)
    }
})

const isModal = ref(false)

const showModal = (): void => {
    isModal.value = !isModal.value
}

const breakpointsThumb = {
    '0': {
        slidesPerView: 4.5
    }
}

const { width } = useWindowSize()
const hideImgCount = ref(0)

onMounted(() => {
    window.addEventListener('keydown', useEscClose(closeWishlistModal))

    if (width.value > 1400) {
        hideImgCount.value = 8
        return
    } else if (width.value > 991) {
        hideImgCount.value = 7
        return
    } else {
        hideImgCount.value = 5
        return
    }
})

interface ExtractedFeature {
    title: string;
    items: FeatureItem[];
}

type FeaturesKeys = keyof Features;

const processItems = (items: FeatureItem[] | { [key: string]: FeatureItem }): FeatureItem[] => {
    return Array.isArray(items) ? items.slice(0, 4) : Object.values(items).slice(0, 4)
}

const processFreebiesIncluded = (items: FreebiesIncluded[] | { [key: string]: FreebiesIncluded }): FeatureItem[] => {
    if (Array.isArray(items)) {
        return items.map(item => ({
            displayType: item.displayType as string,
            label: item.label,
            value: item.value || ''
        })).slice(0, 4)
    } else {
        return Object.values(items).map(item => ({
            displayType: item.displayType as string,
            label: item.label,
            value: item.value || ''
        })).slice(0, 4)
    }
}

const extractFeatures = (features: Features): ExtractedFeature[] => {
    const result: ExtractedFeature[] = []
    const amenities = features.amenities

    if (amenities) {
        const amenityKeys = Object.keys(amenities) as Array<keyof Amenities>
        if (amenityKeys.length >= 2) {
            for (const key of amenityKeys.slice(0, 2)) {
                let items: FeatureItem[]
                if (key === 'Freebies/Included' || key === 'Not Offered') {
                    items = processFreebiesIncluded(amenities[key] as FreebiesIncluded[] | {
                        [key: string]: FreebiesIncluded
                    })
                } else {
                    items = processItems(amenities[key] as { [key: string]: FeatureItem })
                }

                result.push({
                    title: key,
                    items: items
                })
            }
            return result
        } else if (amenityKeys.length === 1) {
            const key = amenityKeys[0]
            let items: FeatureItem[]
            if (key === 'Freebies/Included' || key === 'Not Offered') {
                items = processFreebiesIncluded(amenities[key] as FreebiesIncluded[] | {
                    [key: string]: FreebiesIncluded
                })
            } else {
                items = processItems(amenities[key] as { [key: string]: FeatureItem })
            }

            result.push({
                title: key,
                items: items
            })

            for (const mainKey in features) {
                if (mainKey !== 'amenities') {
                    const category = features[mainKey as keyof Omit<Features, 'amenities'>]
                    for (const subKey in category) {
                        const subItems = processItems(category[subKey as keyof typeof category])
                        result.push({
                            title: subKey,
                            items: subItems
                        })
                        break
                    }
                    break
                }
            }
            return result
        }
    }

    const processCategory = (category: GoodFor | Details | RoomTypes | Allowed | Restrictions): void => {
        for (const subKey in category) {
            const items = processItems(category[subKey as keyof typeof category])
            result.push({
                title: subKey,
                items: items
            })
            if (result.length >= 2) break
        }
    }

    for (const key in features) {
        if (key !== 'amenities' && features[key as FeaturesKeys]) {
            const category = features[key as FeaturesKeys] as GoodFor | Details | RoomTypes | Allowed | Restrictions
            processCategory(category)
        }
        if (result.length >= 2) break
    }

    return result
}

const formattedFeatures = computed<ExtractedFeature[]>(() => extractFeatures(props.pageFeatures))

const listingNeighborhoodTitle = computed(() => {
    if (props.listing.cityAlt && props.listing.distanceToCityCenter) {
        return `Distance to city center: ${ props.listing.distanceToCityCenter } km - ${ props.listing.cityAlt }`
    } else if (props.listing.cityAlt) {
        return props.listing.cityAlt
    } else if (props.listing.distanceToCityCenter) {
        return `Distance to city center: ${ props.listing.distanceToCityCenter } km`
    } else {
        return ''
    }
})

//wishlist
const showWishlistModal = ref(false)

const closeWishlistModal = (): void => {
    showWishlistModal.value = false
}

const openWishlistModal = (): void => {
    showWishlistModal.value = true
}

const enteredEmail = ref('')

const updateEnteredEmail = (event: Event): void => {
    const target = event.target as HTMLInputElement

    enteredEmail.value = target.value
}

const enteredPassword = ref('')

const updateEnteredPassword = (event: Event): void => {
    const target = event.target as HTMLInputElement

    enteredPassword.value = target.value
}

const rememberMeCheckbox = ref(false)

const updateRememberMeCheckbox = (event: Event): void => {
    const target = event.target as HTMLInputElement

    rememberMeCheckbox.value = target.checked
}

const clearInputs = (): void => {
    enteredPassword.value = ''
    enteredEmail.value = ''
    rememberMeCheckbox.value = false
}

const submitModalLoginForm = (): void => {

    const request = {
        url: '/login',
        username: enteredEmail.value,
        password: enteredPassword.value,
        remember: rememberMeCheckbox.value
    }

    loginEndpoint(request, (data) => {
        setAuthorizationStatus(data)

        if (data.user) {
            toggleIsUserLogged(true)

            setUserInfo(data.user)

            closeWishlistModal()

            enteredPassword.value = ''

            enteredEmail.value = ''

            clearAuthorizationFormData()

            const request = {
                url: '/wishlists/isActive'
            }

            wishlistsIsActiveEndpoint(request, (data) => {

                if (data.listings) {
                    setUserWishListLocationsIDs(data.listings)
                }
            })
        }
    })
}

const goToComparePriceTab = (): void => {
    showModal()
    emit('tabsSwitchAndScroll', ListingTabs.ComparePrices)
}

const getBrandLinkClass = (systemShortName: string): string => {
    if (systemShortName === 'Hostelworld') {
        return 'btn-hw'
    } else if (systemShortName === 'Booking.com') {
        return 'btn-booking'
    } else {
        return 'listing-bottom-link'
    }
}

onUnmounted(() => {
    clearAuthorizationFormData()

    window.removeEventListener('keydown', useEscClose(closeWishlistModal))
})

const shouldUseFlexColumn = computed(() => {
    const entries = Object.values(props.pageContactActiveImports)
    const notListedEntries = entries.filter(entry => !entry.isListed)

    if (entries.length === 2 && notListedEntries.length === 1) {
        return false
    }

    return notListedEntries.length === 1 || notListedEntries.length === 2
})
</script>

<template>
    <section v-if="listing.specialNote" class="bg-accent py-5 px-4 my-3">
        {{ listing.specialNote }}
    </section>
    <section
        class="d-flex listing-top justify-content-between mb-md-5 mb-3"
        :class="{'align-items-center' : String(listing.formatCombinedRating) !== '0.0' && String(listing.combinedRatingCount) !== '0'}"
    >
        <div class="listing-top-wrap">
            <div
                class="d-flex flex-row justify-content-between mb-3 listing-top-inner"
            >
                <div class="w-100">
                    <div class="d-flex justify-content-between align-items-center align-items-lg-start w-100">
                        <h1 class="font-weight-bold listing-top-title mr-sm-0 mr-2">
                            {{ listing.name }}{{ listing.city ? `, ${ listing.city }` : '' }}
                        </h1>

                        <div
                            v-if="!hostelIsClosed"
                            class="listing-top-wrap-icons ml-2 flex-shrink-0"
                            :class="{'align-items-start' : String(listing.formatCombinedRating) !== '0.0'}"
                        >
                            <LocationsAtUserWishLists
                                :user-wish-list-locations-i-ds="userWishListLocationsIDs"
                                :hostel-id="props.listingID"
                                @open-wishlist-modal="openWishlistModal"
                            />

                            <ComparisonIcon :listing-id="props.listingID" />

                            <div
                                v-if="String(listing.formatCombinedRating) !== '0.0' && String(listing.combinedRatingCount) !== '0'"
                                class="d-lg-flex flex-column d-none align-items-center justify-content-center ml-2"
                            >
                                <div
                                    class="listing-top-rating mb-1"
                                    @click="emit('tabsSwitchAndScroll', ListingTabs.Reviews)"
                                >
                                    <span
                                        class="combinedRating cursor-pointer"
                                        :content="listing.formatCombinedRating"
                                    >
                                        {{ listing.formatCombinedRating }}
                                    </span>
                                </div>

                                <div
                                    class="nowrap cl-subtext listing-top-rating-reviews cursor-pointer"
                                    @click="emit('tabsSwitchAndScroll', ListingTabs.Reviews)"
                                >
                                    <span>{{ listing.combinedRatingCount }} reviews</span>
                                </div>
                            </div>
                            <p
                                v-else
                                class="d-lg-block d-none cl-subtext font-weight-bold tx-body mb-0 listing-top-no-rating-desktop cursor-pointer flex-shrink-0"
                                @click="emit('tabsSwitchAndScroll', ListingTabs.Reviews)"
                            >
                                No Reviews yet.
                                <br>
                                Be the first!
                            </p>
                        </div>
                    </div>


                    <div v-if="!hostelIsClosed" class="listing-top-neighborhood-wrap mt-lg-3 mt-2">
                        <div
                            v-if="listing.cityAlt || listing.distanceToCityCenter"
                            class="listing-top-neighborhood-inner cursor-pointer"
                            @click="emit('tabsSwitchAndScroll', ListingTabs.Map)"
                        >
                            <TheIcon icon-id="map-place" width="24" height="24" />

                            <span class="listing-top-neighborhood-title">{{ listingNeighborhoodTitle }}</span>
                        </div>

                        <div
                            v-if="String(listing.formatCombinedRating) !== '0.0' && String(listing.combinedRatingCount) !== '0'"
                            class="pl-0 pl-lg-5 text-center d-lg-none"
                        >
                            <div
                                class="list-footer_rating d-flex flex-column align-items-center justify-content-center"
                            >
                                <div
                                    class="hostel-card-rating hostel-card-rating-small listing-top-rating-small mb-1 cursor-pointer"
                                    @click="emit('tabsSwitchAndScroll', ListingTabs.Reviews)"
                                >
                                    {{ listing.formatCombinedRating }}
                                </div>

                                <div
                                    class="pre-title cl-subtext nowrap cursor-pointer"
                                    @click="emit('tabsSwitchAndScroll', ListingTabs.Reviews)"
                                >
                                    <span property="ratingCount">{{ listing.combinedRatingCount }} reviews</span>
                                </div>
                            </div>
                        </div>

                        <p
                            v-else
                            class="cl-subtext text-uppercase pre-title mb-0 text-right d-lg-none cursor-pointer ml-auto"
                            @click="emit('tabsSwitchAndScroll', ListingTabs.Reviews)"
                        >
                            No Reviews yet. Be the first!
                        </p>
                    </div>
                </div>
            </div>

            <div
                v-if="pageFeatures.goodFor && pageFeatures.goodFor['Best Recommended For']"
                class="listing-top-feature-wrap "
                :class="{'mb-lg-0': !listing.labels.isBoutique && !listing.labels.isCheapest && !listing.labels.isTopRated && !listing.labels.isFemaleOnly}"
            >
                <span
                    v-for="(feature, index) in pageFeatures.goodFor['Best Recommended For']"
                    :key="`${ feature }_${ index }`"
                    class="listing-top-feature"
                >
                    {{ feature.label }}
                </span>
            </div>

            <div
                v-if="listing.labels.isBoutique || listing.labels.isCheapest || listing.labels.isTopRated || listing.labels.isFemaleOnly"
                class="d-flex align-items-md-center mb-lg-0 mb-3 flex-column flex-md-row  listing-top-honors"
            >
                <div v-if="listing.labels.isBoutique" class="d-flex align-items-center">
                    <TheIcon
                        icon-id="boutique-icon"
                        width="24"
                        height="24"
                        class="d-flex"
                    />

                    <span class="ml-2 tx-small cl-subtext">A true Boutique Hostel</span>
                </div>

                <div v-if="listing.labels.isFemaleOnly" class="d-flex align-items-center female-only">
                    <span class="ml-2 tx-small cl-subtext">Women-Only Hostel</span>
                </div>

                <div v-if="listing.labels.isTopRated" class="d-flex align-items-center">
                    <TheIcon
                        icon-id="top-rated-icon"
                        width="24"
                        height="24"
                        class="d-flex"
                    />

                    <span class="ml-2 tx-small cl-subtext">#1 Top Rated Hostel in {{ listing.city }}</span>
                </div>

                <div v-if="listing.labels.isCheapest" class="d-flex align-items-center">
                    <TheIcon
                        icon-id="boutique-icon"
                        width="24"
                        height="24"
                        class="d-flex"
                    />

                    <span class="ml-2 tx-small cl-subtext">Cheapest Hostel in {{ listing.city }}</span>
                </div>
            </div>

            <div v-if="!hostelIsClosed" class="d-lg-none">
                <div class="d-flex align-items-center justify-content-center mb-2">
                    <h5 class="mb-0 mr-2 font-weight-bold">
                        See How Much You'll Save
                    </h5>
                    <TheTooltip class="listing-top-tooltip" />
                </div>
                <button
                    class="btn btn-danger d-flex ml-auto ml-md-0 border-0 shadow-none btn-lg listing-top-btn justify-content-center w-100"
                    :title="`Compare prices for ${ listing.name }`"
                    @click="emit('tabsSwitchAndScroll', ListingTabs.ComparePrices)"
                >
                    Compare Prices here
                </button>
                <p class="cl-subtext text-uppercase pre-title mt-1 mb-0 pb-3 text-center">
                    with our price comparison
                </p>
                <div class="listing-top-booking-block pt-3">
                    <p class="mb-2 font-weight-bold tx-small text-center">
                        Listed on these booking sites:
                    </p>
                    <div class="d-flex listing-top-sites-block" :class="{'flex-column' : shouldUseFlexColumn}">
                        <div
                            v-for="(link) in pageContactActiveImports"
                            :key="link.systemShortName"
                            class="d-flex justify-content-center w-100"
                            :title="`${listing.name} at ${link.systemShortName}`"
                        >
                            <a
                                v-if="link.isListed"
                                :href="link.href"
                                target="_blank"
                                class="btn w-100 d-flex align-items-center justify-content-center"
                                :class="getBrandLinkClass(link.systemShortName)"
                                rel="nofollow"
                            >
                                <div
                                    v-if="link.systemShortName === 'Hostelworld'"
                                    class="position-relative d-flex align-items-center justify-content-center mr-2"
                                >
                                    <TheIcon
                                        :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                        width="22"
                                        height="22"
                                        class="z-index-1 d-flex flex-shrink-0"
                                    />
                                    <div class="position-absolute hostelworld-icon-bg" />
                                </div>
                                <TheIcon
                                    v-else
                                    :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                    width="22"
                                    height="22"
                                    class="mr-2 d-flex flex-shrink-0"
                                />

                                {{ link.systemShortName }}
                            </a>
                            <a
                                v-else
                                :href="link.href"
                                rel="nofollow"
                                target="_blank"
                                :title="`Not listed at ${ link.systemShortName }`"
                                class="btn w-100 d-flex align-items-center justify-content-center"
                                :class="[getBrandLinkClass(link.systemShortName), {'opacity-5': !link.isListed}]"
                                :placeholder="`Not listed at ${link.systemShortName}`"
                            >
                                <div
                                    v-if="link.systemShortName === 'Hostelworld'"
                                    class="position-relative d-flex align-items-center justify-content-center mr-2"
                                >
                                    <TheIcon
                                        :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                        width="22"
                                        height="22"
                                        class="z-index-1 d-flex flex-shrink-0"
                                    />
                                    <div class="position-absolute hostelworld-icon-bg" />
                                </div>
                                <TheIcon
                                    v-else
                                    :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                    width="22"
                                    height="22"
                                    class="mr-2 d-flex flex-shrink-0"
                                />
                                <span class="font-weight-normal text-lowercase">
                                    Not listed at {{ link.systemShortName }}
                                </span>
                            </a>
                        </div>
                        <div v-if="Object.keys(pageContactActiveImports).length === 1" class="w-100">
                            <button
                                v-if="!pageContactActiveImports.BookingDotCom"
                                class="btn w-100 d-flex align-items-center justify-content-center listing-top-sites-block-static-btn w-100"
                                :class="getBrandLinkClass('Booking.com')"
                            >
                                <TheIcon
                                    icon-id="bookingdotcom-icon-sm"
                                    width="22"
                                    height="22"
                                    class="mr-2 d-flex flex-shrink-0"
                                />

                                <span class="font-weight-normal text-lowercase">
                                    Not listed at Booking.com
                                </span>
                            </button>
                            <button
                                v-else-if="!pageContactActiveImports.BookHostels "
                                class="btn w-100 d-flex align-items-center justify-content-center listing-top-sites-block-static-btn"
                                :class="getBrandLinkClass('Hostelworld')"
                            >
                                <div

                                    class="position-relative d-flex align-items-center justify-content-center mr-2 "
                                >
                                    <TheIcon
                                        icon-id="bookhostels-icon-sm"
                                        width="22"
                                        height="22"
                                        class="mr-2 d-flex flex-shrink-0 z-index-1"
                                    />
                                    <div class="position-absolute hostelworld-icon-bg hostelworld-icon-bg--static" />
                                </div>

                                <span class="font-weight-normal text-lowercase">
                                    Not listed at Hostelworld
                                </span>
                            </button>
                        </div>
                        <div
                            v-else-if="Object.keys(pageContactActiveImports).length === 0"
                            class="d-flex flex-column w-100"
                        >
                            <button
                                class="btn w-100 d-flex align-items-center justify-content-center listing-top-sites-block-static-btn mb-3"
                                :class="getBrandLinkClass('Hostelworld')"
                            >
                                <div
                                    class="position-relative d-flex align-items-center justify-content-center mr-2"
                                >
                                    <TheIcon
                                        icon-id="bookhostels-icon-sm"
                                        width="22"
                                        height="22"
                                        class="mr-2 d-flex flex-shrink-0 z-index-1"
                                    />
                                    <div class="position-absolute hostelworld-icon-bg hostelworld-icon-bg--static" />
                                </div>

                                <span class="font-weight-normal text-lowercase">
                                    Not listed at Hostelworld
                                </span>
                            </button>
                            <button
                                class="btn w-100 d-flex align-items-center justify-content-center  listing-top-sites-block-static-btn"
                                :class="getBrandLinkClass('Booking.com')"
                            >
                                <TheIcon
                                    icon-id="bookingdotcom-icon-sm"
                                    width="22"
                                    height="22"
                                    class="mr-2 d-flex flex-shrink-0"
                                />

                                <span class="font-weight-normal text-lowercase">
                                    Not listed at Booking.com
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="d-lg-none">
                <div class="d-flex flex-column mb-2">
                    <h3 class="mb-0 hostelIsClosed-title">
                        How to book...
                    </h3>
                    <p class="mb-0 font-weight-600 hostelIsClosed-text">
                        your hostels in {{ listing.city }}
                    </p>
                </div>

                <a
                    class="btn btn-danger d-flex justify-content-center ml-auto ml-md-0 border-0 shadow-none btn-lg listing-top-btn hostelIsClosed"
                    :title="`Check Hostelworld`"
                    :href="bookHostelsHref"
                    rel="nofollow"
                    target="_blank"
                >
                    <div class="position-relative d-flex align-items-center justify-content-center mr-2">
                        <TheIcon
                            icon-id="bookhostels-icon-sm-transparent"
                            width="22"
                            height="22"
                            class="z-index-1 d-flex flex-shrink-0"
                        />

                        <div class="position-absolute hostelworld-icon-bg" />
                    </div>
                    Check Hostelworld
                </a>
            </div>
        </div>

        <div
            v-if="!hostelIsClosed"
            class="border-left text-center flex-shrink-0 d-none d-lg-block listing-top-booking-sites-wrap"
        >
            <div class="d-flex flex-column">
                <div class="d-flex align-items-center justify-content-center mb-2">
                    <h5 class="mb-0 mr-2 font-weight-bold">
                        See How Much You'll Save
                    </h5>
                    <TheTooltip class="listing-top-tooltip" />
                </div>

                <button
                    class="btn btn-danger d-flex ml-auto ml-md-0 border-0 shadow-none btn-lg listing-top-btn justify-content-center"
                    :title="`Compare prices for ${ listing.name }`"
                    @click="emit('tabsSwitchAndScroll', ListingTabs.ComparePrices)"
                >
                    Compare Prices here
                </button>
                <p class="cl-subtext text-uppercase pre-title mt-1 mb-0 pb-3">
                    with our price comparison
                </p>
                <div class="listing-top-booking-block pt-3">
                    <p class="mb-2 font-weight-bold tx-small">
                        Listed on these booking sites:
                    </p>
                    <div
                        class="d-flex listing-top-sites-block justify-content-center"
                        :class="{'flex-column' : shouldUseFlexColumn}"
                    >
                        <div
                            v-for="(link) in pageContactActiveImports"
                            :key="link.systemShortName"
                            class="d-flex justify-content-center"
                            :title="`${listing.name} at ${link.systemShortName}`"
                        >
                            <a
                                v-if="link.isListed"
                                :href="link.href"
                                target="_blank"
                                class="btn w-100 d-flex align-items-center justify-content-center"
                                :class="getBrandLinkClass(link.systemShortName)"
                                rel="nofollow"
                            >
                                <div
                                    v-if="link.systemShortName === 'Hostelworld'"
                                    class="position-relative d-flex align-items-center justify-content-center mr-2"
                                >
                                    <TheIcon
                                        :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                        width="22"
                                        height="22"
                                        class="z-index-1 d-flex flex-shrink-0"
                                    />

                                    <div class="position-absolute hostelworld-icon-bg" />
                                </div>
                                <TheIcon
                                    v-else
                                    :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                    width="22"
                                    height="22"
                                    class="mr-2 d-flex flex-shrink-0"
                                />

                                {{ link.systemShortName }}
                            </a>
                            <a
                                v-else
                                :href="link.href"
                                rel="nofollow"
                                target="_blank"
                                :title="`Not listed at ${ link.systemShortName }`"
                                class="btn w-100 d-flex align-items-center justify-content-center"
                                :class="[getBrandLinkClass(link.systemShortName), {'opacity-5': !link.isListed}]"
                                :placeholder="`Not listed at ${link.systemShortName}`"
                            >
                                <div
                                    v-if="link.systemShortName === 'Hostelworld'"
                                    class="position-relative d-flex align-items-center justify-content-center mr-2"
                                >
                                    <TheIcon
                                        :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                        width="22"
                                        height="22"
                                        class="z-index-1 d-flex flex-shrink-0"
                                    />
                                    <div class="position-absolute hostelworld-icon-bg" />
                                </div>
                                <TheIcon
                                    v-else
                                    :icon-id="`${link.systemName.toLowerCase()}-icon-sm`"
                                    width="22"
                                    height="22"
                                    class="mr-2 d-flex flex-shrink-0"
                                />
                                <span class="font-weight-normal text-lowercase">
                                    Not listed at {{ link.systemShortName }}
                                </span>
                            </a>
                        </div>
                        <div v-if="Object.keys(pageContactActiveImports).length === 1">
                            <button
                                v-if="!pageContactActiveImports.BookingDotCom"
                                class="btn w-100 d-flex align-items-center justify-content-center  listing-top-sites-block-static-btn"
                                :class="getBrandLinkClass('Booking.com')"
                            >
                                <TheIcon
                                    icon-id="bookingdotcom-icon-sm"
                                    width="22"
                                    height="22"
                                    class="mr-2 d-flex flex-shrink-0"
                                />

                                <span class="font-weight-normal text-lowercase">
                                    Not listed at Booking.com
                                </span>
                            </button>
                            <button
                                v-else-if="!pageContactActiveImports.BookHostels "
                                class="btn w-100 d-flex align-items-center justify-content-center listing-top-sites-block-static-btn"
                                :class="getBrandLinkClass('Hostelworld')"
                            >
                                <div
                                    class="position-relative d-flex align-items-center justify-content-center mr-2 "
                                >
                                    <TheIcon
                                        icon-id="bookhostels-icon-sm"
                                        width="22"
                                        height="22"
                                        class="mr-2 d-flex flex-shrink-0 z-index-1"
                                    />
                                    <div class="position-absolute hostelworld-icon-bg hostelworld-icon-bg--static" />
                                </div>

                                <span class="font-weight-normal text-lowercase">
                                    Not listed at Hostelworld
                                </span>
                            </button>
                        </div>
                        <div
                            v-else-if="Object.keys(pageContactActiveImports).length === 0"
                            class="d-flex flex-column gap-2"
                        >
                            <button
                                class="btn w-100 d-flex align-items-center justify-content-center listing-top-sites-block-static-btn mb-3"
                                :class="getBrandLinkClass('Hostelworld')"
                            >
                                <div
                                    class="position-relative d-flex align-items-center justify-content-center mr-2 "
                                >
                                    <TheIcon
                                        icon-id="bookhostels-icon-sm"
                                        width="22"
                                        height="22"
                                        class="mr-2 d-flex flex-shrink-0 z-index-1"
                                    />

                                    <div class="position-absolute hostelworld-icon-bg hostelworld-icon-bg--static" />
                                </div>

                                <span class="font-weight-normal text-lowercase">
                                    Not listed at Hostelworld
                                </span>
                            </button>
                            <button
                                class="btn w-100 d-flex align-items-center justify-content-center  listing-top-sites-block-static-btn"
                                :class="getBrandLinkClass('Booking.com')"
                            >
                                <TheIcon
                                    icon-id="bookingdotcom-icon-sm"
                                    width="22"
                                    height="22"
                                    class="mr-2 d-flex flex-shrink-0"
                                />

                                <span class="font-weight-normal text-lowercase">
                                    Not listed at Booking.com
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="pl-lg-2 pl-xl-5  border-left text-center flex-shrink-0 d-none d-lg-block">
            <h3 class="mb-3">
                How to book...
            </h3>
            <p class="mb-3 font-weight-600">
                your hostels in {{ listing.city }}
            </p>
            <a
                class="btn btn-danger d-flex ml-auto ml-md-0 border-0 shadow-none btn-lg listing-top-btn hostelIsClosed"
                :title="`Check Hostelworld`"
                :href="bookHostelsHref"
                rel="nofollow"
                target="_blank"
            >
                <div class="position-relative d-flex align-items-center justify-content-center mr-2">
                    <TheIcon
                        icon-id="bookhostels-icon-sm-transparent"
                        width="22"
                        height="22"
                        class="z-index-1 d-flex flex-shrink-0"
                    />

                    <div class="position-absolute hostelworld-icon-bg" />
                </div>
                Check Hostelworld
            </a>
        </div>
    </section>

    <section class="listing-top-bottom-section mb-md-3 mb-5 d-flex flex-md-row flex-column">
        <div class="listing-top-bottom-section-left order-md-0 order-1">
            <div class="d-flex align-items-center justify-content-between mb-3">
                <p class="mr-1 mb-0 tx-body font-weight-600">
                    Facilities and Amenities
                </p>
                <button
                    type="button"
                    class="btn btn-lg btn-light listing-top-bottom-section-left-btn flex-shrink-0"
                    @click="emit('tabsSwitchAndScroll', ListingTabs.FacilitiesAndAmenities)"
                >
                    View all
                </button>
            </div>
            <div class="d-flex flex-column listing-top-bottom-section-left-lists-wrap">
                <ListingList :divider="true" :page-features="formattedFeatures" />


                <h5 class="pre-title cl-subtext mb-3">
                    Location
                </h5>
                <div class="listing-top-bottom-section-left-list-location">
                    <img src="../../../../../public/images/map-overlay.png" alt="map-overlay">
                    <button
                        class="btn btn-lg btn-light listing-top-bottom-section-left-list-location-btn border-0 d-flex align-items-center justify-content-between"
                        @click="emit('tabsSwitchAndScroll', ListingTabs.Map)"
                    >
                        <TheIcon
                            icon-id="map-place"
                            width="24"
                            height="24"
                            class="d-flex"
                        />

                        <span>Show map</span>
                    </button>
                </div>
            </div>
        </div>
        <div class="listing-top-bottom-section-right d-md-block d-none w-100">
            <div class="listing-top-bottom-section-right-img-wrap-top d-flex flex-column flex-lg-row">
                <div
                    v-if="listing.images[0] && listing.images[0].src"
                    class="listing-top-bottom-section-right-main-img"
                    @click="showModal"
                >
                    <picture>

                        <source :srcset="listing.images[0].src.big_webp" type="image/webp">
                        <source :srcset="listing.images[0].src.big" type="image/jpg">

                        <img
                            :alt="listing.images[0].title"
                            :title="listing.images[0].title"
                        >
                    </picture>
                </div>
                <div class="listing-top-bottom-section-right-secondary-img-wrap d-flex flex-lg-column">
                    <div
                        v-if="listing.images[1] && listing.images[1].src"
                        class="listing-top-bottom-section-right-secondary-img"
                        @click="showModal"
                    >
                        <picture>

                            <source :srcset="listing.images[1].src.thumb_webp" type="image/webp">
                            <source :srcset="listing.images[1].src.thumb_def" type="image/jpg">

                            <img
                                :alt="listing.images[1].title"
                                :title="listing.images[1].title"
                            >
                        </picture>
                    </div>
                    <div
                        v-if="listing.images[2] && listing.images[2].src"
                        class="listing-top-bottom-section-right-secondary-img"
                        @click="showModal"
                    >
                        <picture>

                            <source :srcset="listing.images[2].src.thumb_webp" type="image/webp">
                            <source :srcset="listing.images[2].src.thumb_def" type="image/jpg">

                            <img
                                :alt="listing.images[2].title"
                                :title="listing.images[2].title"
                            >

                        </picture>
                    </div>
                </div>
            </div>
            <div v-if="displayedImages" class="d-flex listing-top-bottom-section-right-img-wrap-bottom">
                <div
                    v-for="(img) in displayedImages"
                    :key="img.src.thumb_def"
                    class="listing-top-bottom-section-right-tiny-img mr-3"
                    @click="showModal"
                >
                    <picture v-if="img.src">
                        <source :srcset="img.src.thumb_webp" type="image/webp">
                        <source :srcset="img.src.thumb_def" type="image/jpg">

                        <img
                            :alt="img.title"
                            :title="img.title"
                        >
                    </picture>
                </div>

                <div
                    v-if="listing.images.length && listing.images.length > hideImgCount"
                    class="listing-top-bottom-section-right-img-wrap-bottom-label d-flex justify-content-center align-items-center"
                    @click="showModal"
                >
                    <span>+{{ listing.images.length - hideImgCount }} images</span>
                </div>
            </div>
        </div>
        <div class="d-md-none listing-top-bottom-section-right-swiper">
            <ListingSwiper
                v-if="listing.images"
                :listing-images="listing.images"
                :pagination-main="{ clickable: true, dynamicBullets: true }"
                :space-between-thumb="8"
                :breakpoints-thumb="breakpointsThumb"
            />
        </div>
    </section>

    <AppModal
        :show="isModal"
        class="listing-top-modal"
        @close="showModal"
    >
        <ListingModalContent
            :listing="listing"
            :reviews="pageReviews"
            :book-hostels-href="bookHostelsHref"
            :hostel-is-closed="hostelIsClosed"
            @tabs-switch-and-scroll="emit('tabsSwitchAndScroll', ListingTabs.ComparePrices)"

            @close-modal="showModal"
        >
            <template #header>
                <h2 class="listing-top-modal-title mb-0 mr-3">
                    {{ listing.name }}{{ listing.city ? `, ${ listing.city }` : '' }}
                </h2>
                <a
                    v-if="!hostelIsClosed"
                    href="#"
                    class="btn btn-danger d-flex ml-auto ml-md-0 border-0 shadow-none btn-lg listing-top-modal-btn justify-content-center"

                    :title="`book now ${ listing.name }`"
                    @click.prevent="goToComparePriceTab"
                >
                    Check availability
                </a>
                <a
                    v-else
                    class="btn btn-danger d-flex ml-auto ml-md-0 border-0 shadow-none btn-lg hostelIsClosed"
                    :title="`Check Hostelworld`"
                    :href="bookHostelsHref"
                    rel="nofollow"
                    target="_blank"
                >
                    Check Hostelworld
                </a>
            </template>
        </ListingModalContent>
        <template #close-icon>
            <div class="close-modal-btn d-flex align-items-center justify-content-center">
                <TheIcon
                    icon-id="close-icon-2"
                    width="24"
                    height="24"
                    stroke="#4A5268"
                />
            </div>
        </template>
    </AppModal>

    <AppModal
        :show="showWishlistModal"
        class="login-modal-listing-top"
    >
        <TwoFieldsLoginModalContent
            :entered-email="enteredEmail"
            :entered-password="enteredPassword"
            :remember-me-checkbox="rememberMeCheckbox"
            :error-status-from-server="errorStatusFromServer"
            :is-user-logged="isUserLogged"
            :is-loading="isLoadingRequestWishlist"
            @clear-inputs="clearInputs"
            @clear-authorization-form-data="clearAuthorizationFormData"
            @close-modal="closeWishlistModal"
            @submit-modal-login-form="submitModalLoginForm"
            @update-entered-email="updateEnteredEmail"
            @update-entered-password="updateEnteredPassword"
            @update-remember-me-checkbox="updateRememberMeCheckbox"
        />

        <template #close-icon>
            <span class="close-modal-btn--wishlist">&times;</span>
        </template>
    </AppModal>
</template>

<style lang="scss" scoped>
.listing-top {
    gap: 32px;

    &-booking-block {
        border-top: 1px solid var(--accent-bg);
    }

    &-booking-sites-wrap {
        padding-left: 24px;
        @media (min-width: 1200px) {
            padding-left: 32px;
        }
    }

    &-sites-block {
        gap: 8px;
        flex-wrap: wrap;

        @media (min-width: 380px) {
            gap: 16px;
            flex-wrap: nowrap;
        }

        &-static-btn {
            opacity: 0.5;
            cursor: auto;

            &:hover {
                opacity: 0.5;
            }
        }
    }

    &-modal {
        &-title {
            font-family: var(--font-family-poppins-sans-serif);
            font-weight: 600;
            line-height: 1.4;
            font-size: 20px;

            @media (min-width: 992px) {
                font-size: 24px;
            }
        }

        &-btn {
            border-radius: 8px;
            padding: 0.5rem 1rem;
            color: white;

            @media (min-width: 992px) {
                padding: 0.75rem 1.5rem;
            }
        }
    }

    &-no-rating-desktop {
        //margin-top: 11px;
    }

    &-wrap {
        width: 100%;

        &-icons {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    &-inner {
        margin-top: 16px;

        @media (min-width: 768px) {
            margin-top: 0px;
        }
    }

    &-honors {
        gap: 16px;

        @media (min-width: 768px) {
            gap: 32px;
        }
    }

    &-neighborhood {

        &-wrap {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
        }

        &-inner {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        &-title {
            font-weight: 700;
            font-size: 10px;
            line-height: 15px;
            color: #787D8D;

            @media (min-width: 992px) {
                font-weight: 600;
                font-size: 16px;
                line-height: 1.4;
            }
        }
    }

    &-title {
        margin-bottom: 0;

        font-family: var(--font-family-poppins-sans-serif);
        font-size: 24px;
        font-weight: 600;
        line-height: 1.4;

        @media (min-width: 768px) {
            font-size: 32px;
        }

        @media (min-width: 992px) {
            font-family: var(--font-family-montserrat-sans-serif);
            font-size: 40px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: -0.02em;
        }
    }

    &-feature-wrap {
        margin-bottom: 16px;
    }

    &-feature {
        font-weight: 700;
        font-size: 10px;
        line-height: 15px;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: #787D8D;

        & + .listing-top-feature::before {
            display: inline-block;
            padding: 0 0.5rem;
            color: #787D8D;
            content: "·";
        }
    }

    &-description {
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 1.4;
        word-break: break-word;
        overflow-wrap: break-word;
        color: #787D8D;
        margin-bottom: 0;

        &-wrap {
            margin-bottom: 16px;

            &:deep(*) {
                font-style: normal;
                font-weight: normal;
                font-size: 14px;
                line-height: 1.4;
                word-break: break-word;
                overflow-wrap: break-word;
                color: #787D8D;
                margin: 0;
                padding: 0;
            }
        }

        &-link {
            color: #4A5268;
            text-transform: lowercase;
            font-weight: 600;
        }
    }

    &-rating {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #4A5268;
        border-radius: 50%;
        width: 44px;
        height: 44px;

        span {
            color: #fff;
            font-weight: 600;
            font-size: 24px;
            line-height: 36px;
        }


        &-reviews {
            font-size: 16px;
            font-weight: 600;
            line-height: 22px;
        }

        &-small {
            width: 33px;
            height: 33px;
            font-size: 16px;
            font-weight: 600;
            line-height: 22px;
            text-align: center;
        }
    }

    &-btn {
        border-radius: 8px;
    }

    &:deep(.comparison-tooltip) {
        right: 130px;

        &::before {
            left: 93%;
        }

        @media (min-width: 992px) {
            right: 60%;

            &::before {
                left: 53%;
            }
        }
    }

    &:deep(.locations-at-user-wishlists-tooltip) {
        right: 45px;

        &::before {
            left: 63%;
        }

        @media (min-width: 992px) {
            right: 25px;

            &::before {
                left: 53%;
            }
        }
    }

    &:deep(.locations-at-user-wishlists-icon) {
        background-color: #f8f9fa;
    }

    &-bottom-section {
        gap: 16px;

        &-left {
            flex-shrink: 0;

            @media (min-width: 768px) {
                max-width: 235px;
            }

            @media (min-width: 992px) {
                max-width: 250px;
            }

            @media (min-width: 1200px) {
                max-width: 280px;
            }

            @media (min-width: 1400px) {
                max-width: 316px;
            }

            &-btn {
                border-radius: 8px;
                padding: 11px 1.5rem;
            }

            &-lists-wrap {
                padding: 1rem;
                border-radius: 1rem;
                border: 1px solid var(--accent-bg);

                &:deep(.listing-list:nth-child(1)) {
                    .listing-list-wrap {
                        span {
                            font-weight: 600;
                            color: var(--main-text);
                        }
                    }
                }

                &:deep(.listing-list-title) {
                    font-weight: bold;
                    font-size: 10px;
                    line-height: 15px;
                    letter-spacing: 0.03em;
                    text-transform: uppercase;
                    color: var(--secondary-text);
                }
            }

            &-list {
                border-bottom: 1px solid var(--accent-bg);

                &-location {
                    position: relative;
                    border-radius: 4px;
                    overflow: hidden;

                    img {
                        width: 100%;
                    }

                    &-btn {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        box-shadow: 0px 6px 2px -4px #0E0E2C14;
                        max-width: 165px;
                        width: 100%;
                        padding: 12px 24px 12px 16px;

                        span {
                            font-family: var(--font-family-poppins-sans-serif);
                            font-size: 16px;
                            font-weight: 700;
                            line-height: 24px;
                            letter-spacing: 0.03em;
                            color: var(--main-text);
                        }

                        &:deep(.the-icon-component) {
                            svg {
                                filter: brightness(0);
                            }
                        }
                    }
                }
            }
        }

        &-right {
            &-img-wrap-top {
                gap: 16px;
            }

            &-main-img {
                height: 440px;
                max-width: 648px;
                cursor: pointer;
                width: 100%;

                img {
                    object-fit: cover;
                    max-width: 100%;
                    height: 100%;
                    width: 100%;
                }
            }

            &-secondary-img {
                max-width: 316px;
                height: 212px;
                cursor: pointer;
                width: 100%;

                img {
                    object-fit: cover;
                    max-width: 100%;
                    height: 100%;
                    width: 100%;
                }


                &-wrap {
                    gap: 16px;
                }
            }

            &-tiny-img {
                max-width: 173px;
                height: 173px;
                flex: 0 0 auto;
                cursor: pointer;

                img {
                    object-fit: cover;
                    max-width: 100%;
                    height: 100%;
                }
            }

            &-img-wrap-bottom {
                position: relative;
                margin-top: 16px;
                overflow: hidden;
                white-space: nowrap;

                &-label {
                    position: absolute;
                    background-color: rgba(0, 0, 0, 0.5);
                    height: 173px;
                    cursor: pointer;
                    right: 0;

                    @media (min-width: 768px) {
                        width: 250px;
                    }

                    @media (min-width: 992px) {
                        width: 97px;
                    }

                    @media (min-width: 1200px) {
                        width: 247px;
                    }

                    @media (min-width: 1400px) {
                        width: 202px;
                    }

                    span {
                        color: white;
                        font-size: 16px;
                        line-height: 1.4;
                    }
                }
            }

            &-swiper {
                &:deep(.listing-swiper) {
                    height: 240px;

                    @media (min-width: 576px) {
                        height: 340px;
                    }

                    .swiper-pagination {
                        bottom: 16px;
                    }

                    img {
                        max-height: 320px;
                        border-radius: 16px;

                        @media (min-width: 576px) {
                            min-height: 340px;
                        }
                    }
                }

                &:deep(.listing-swiper-thumb) {
                    margin-top: 8px;

                    img {
                        border-radius: 8px;
                        height: 72px;

                        @media (min-width: 576px) {
                            height: 120px
                        }
                    }
                }
            }
        }
    }

    &:deep(.locations-at-user-wishlists-icon-heart) {
        background-color: #f8f9fa;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .hostelIsClosed {
        background-color: #ff733d;
        transition: background-color 0.3s;
        cursor: pointer;
        color: white;
        border-radius: 8px;

        &:hover {
            background-color: #cc5c30;
            border-color: #cc5c30;
            color: white;


            &:deep(.the-icon-component) {
                svg {
                    fill: #cc5c30;
                }
            }
        }

        &-title {
            font-family: var(--font-family-poppins-sans-serif);
            line-height: 1.5;
            font-size: 20px;
            font-weight: 600;

            @media (min-width: 768px) {
                font-size: 1.5rem;
            }
        }

        &-text {
            font-family: var(--font-family-poppins-sans-serif);
            font-size: 16px;

            @media (min-width: 768px) {
                font-size: 18px;
            }
        }

        &:deep(.the-icon-component) {
            svg {
                transition: fill 0.3s;
                fill: #ff733d;
            }
        }
    }

    .female-only {
        background-color: #fce8f1;
        padding: 2px 8px;
        border-radius: 4px;
        transition: all 0.3s ease;
        color: #000000;
    }
}

.close-modal-btn {
    border-radius: 50%;
    width: 44px;
    height: 44px;
    background-color: var(--light-bg);

    box-shadow: 0px 6px 2px -4px #0E132C14;

    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:hover {
        background-color: #e2e6ea;
    }

    &:deep(svg) {
        display: flex;
    }

    &--wishlist {
        color: #000;
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1;
        opacity: .5;
        text-shadow: 0 1px 0 #fff;

        transition: color 0.3s, opacity 0.3s;
        display: flex;

        &:hover {
            opacity: 1;
        }
    }
}

.hostelworld-icon-bg {
    background-color: white;
    width: 16px;
    height: 16px;
    z-index: 0;

    &--static {
        left: 2px;
    }
}

.btn-hw,
.btn-booking {
    padding: 7px 11px;
    font-size: 14px;
}
</style>


<style lang="scss">
.listing-top-modal .app-modal-dialog {
    border-radius: 16px;
    height: 95vh;
    width: 95vw;
}

.listing-top-modal .app-modal-close-btn {
    top: 20px;
    right: 24px;

    @media (min-width: 992px) {
        top: 26px;
    }
}

.listing-top-modal .app-modal {
    z-index: 1600;
}

.listing-top-modal {
    .slide-fade-enter-active, .slide-fade-leave-active {
        transition: transform 0.4s ease-out, opacity .5s ease-out;

        .app-modal-dialog {
            transition: transform 0.4s ease-out, opacity .5s ease-out;
        }
    }

    .slide-fade-enter-from {
        opacity: 0;

        .app-modal-dialog {
            transform: translateY(120%);
            opacity: 1;
        }
    }

    .slide-fade-enter-to {
        opacity: 1;

        .app-modal-dialog {
            transform: translateY(0);
            opacity: 1;

        }
    }

    .slide-fade-leave-from {
        opacity: 1;

        .app-modal-dialog {
            transform: translateY(0);
            opacity: 1;

        }
    }

    .slide-fade-leave-to {
        opacity: 0;

        .app-modal-dialog {
            transform: translateY(120%);
            opacity: 1;
        }
    }
}

//wishlist modal
.login-modal-listing-top {

    .app-modal {
        display: block;
        overflow-x: hidden;
        overflow-y: auto;
        z-index: 1600;
    }

    .app-modal-close-btn {

        &:hover {
            .close-modal-btn {
                opacity: 1;
            }
        }
    }

    .app-modal-dialog {
        width: 100%;
        max-width: 100%;
        padding: 16px 8px;
        background-color: transparent;
        box-shadow: none;
        min-height: calc(100% - 1rem);
        display: flex;
        align-items: center;

        @media (min-width: 576px) {
            margin: 1.75rem auto;
            min-height: calc(100% - 3.5rem);
            padding: 0;
            max-width: 500px;
        }
    }

    .app-modal-close-btn {
        top: 36px;
        right: 26px;
    }
}
</style>